/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { toast } from "react-toastify";
import { Middleware } from "redux";

import { TOAST_DEFAULT_ARGS } from "src/lib/constants";
import { api } from "src/lib/utils/api";
import { AppState } from "src/store";

/**
 * Middleware that handles API calls.
 * It is used to make API calls and dispatch actions based on the result.
 */
export const apiMiddleware: Middleware<AppState> =
  ({ dispatch }) =>
  (next) =>
  async (action) => {
    if (action.type !== "apiCallBegan") {
      return next(action);
    }

    const { url, method, data, onStart, onSuccess, onFailToast, onFail, onSuccessToast } = action.payload;

    if (onStart) {
      dispatch(onStart());
    }
    // immediately trigger next action
    next(action);

    // then start API call
    try {
      const response: any = await api(method, url, data);
      dispatch(onSuccess(response));
      if (onSuccessToast) {
        toast.info(onSuccessToast(response?.message), TOAST_DEFAULT_ARGS);
      }
    } catch (error: any) {
      if (onFailToast) {
        toast.error(onFailToast(error.message), TOAST_DEFAULT_ARGS);
      }
      if (onFail) {
        dispatch(onFail(error));
      }
    }
  };
